/**
 * Enemy Entity and AI State Machine: "Bertahan Dari Anomali"
 * 
 * Implements:
 * - AI States: IDLE, PATROL, CHASE, WINDUP (Telegraph Red Zone), ATTACK, RECOVERY, HIT, DEAD.
 * - Strict Red Zone rule: Red warning zone is created on WINDUP, and IMMEDIATELY REMOVED when ATTACK begins.
 * - 4 Archetypes: Melee Axeman, Heavy Brute with Shield, Ranged Archer, Fast Spearman.
 * - Enemy projectiles (Arrows) for Ranged Anomaly.
 */

import { EnemyType, ENEMY_CONFIGS, EnemyStats } from "../config/gameConfig";
import { HitboxShape, ConeShape, RectShape, CircleShape, CombatHitbox } from "../combat/HitboxSystem";
import { TelegraphSystem } from "../combat/TelegraphSystem";
import { GameMap } from "../world/GameMap";
import { anomaliAudio } from "../audio/AnomaliAudio";
import { assetManager } from "../engine/AssetManager";

export type EnemyAIState =
  | "idle"
  | "patrol"
  | "chase"
  | "windup"
  | "attack"
  | "recovery"
  | "hit"
  | "dead";

export interface EnemyArrow {
  id: string;
  x: number;
  y: number;
  vx: number;
  vy: number;
  angle: number;
  damage: number;
  knockback: number;
  active: boolean;
}

export class Enemy {
  public id: string;
  public type: EnemyType;
  public stats: EnemyStats;

  public x: number;
  public y: number;
  public facingAngle: number = 0;

  public hp: number;
  public maxHp: number;
  public isDead: boolean = false;

  // AI & State
  public state: EnemyAIState = "idle";
  public stateTimeMs: number = 0;
  public patrolTimerMs: number = 0;
  public patrolVx: number = 0;
  public patrolVy: number = 0;

  // Attack execution & Hitboxes
  public attackHitSpawned: boolean = false;
  public activeHitboxes: CombatHitbox[] = [];
  public arrows: EnemyArrow[] = [];
  public currentTelegraphShape: HitboxShape | null = null;

  // Hit reaction & Knockback
  public hurtFlashMs: number = 0;
  public knockbackVx: number = 0;
  public knockbackVy: number = 0;

  // Visual anim clock
  public walkAnimClock: number = 0;

  constructor(id: string, type: EnemyType, startX: number, startY: number) {
    this.id = id;
    this.type = type;
    this.stats = ENEMY_CONFIGS[type];
    this.x = startX;
    this.y = startY;
    this.hp = this.stats.maxHp;
    this.maxHp = this.stats.maxHp;
    this.facingAngle = Math.random() * Math.PI * 2;
  }

  /**
   * Take damage from player attack
   */
  public takeDamage(damage: number, kbVx: number, kbVy: number, telegraphSystem: TelegraphSystem): boolean {
    if (this.isDead) return false;

    this.hp = Math.max(0, this.hp - damage);
    this.hurtFlashMs = 180;
    this.knockbackVx = kbVx;
    this.knockbackVy = kbVy;

    // If taking heavy damage during windup, stagger slightly
    if (this.state === "windup" && damage > 50) {
      telegraphSystem.clearTelegraph(this.id);
      this.state = "hit";
      this.stateTimeMs = 0;
    }

    if (this.hp <= 0) {
      this.isDead = true;
      this.state = "dead";
      this.stateTimeMs = 0;
      telegraphSystem.clearTelegraph(this.id);
      anomaliAudio.playSfx("enemy_death");
    } else {
      anomaliAudio.playSfx("hit_enemy");
    }

    return true;
  }

  /**
   * Main Enemy AI Update
   */
  public update(
    dt: number,
    playerX: number,
    playerY: number,
    playerDead: boolean,
    map: GameMap,
    telegraphSystem: TelegraphSystem
  ) {
    const dtMs = dt * 1000;
    this.stateTimeMs += dtMs;

    if (this.hurtFlashMs > 0) this.hurtFlashMs = Math.max(0, this.hurtFlashMs - dtMs);

    // Apply knockback decay
    if (Math.abs(this.knockbackVx) > 1 || Math.abs(this.knockbackVy) > 1) {
      const nextX = this.x + this.knockbackVx * dt;
      const nextY = this.y + this.knockbackVy * dt;
      const res = map.resolveMovement(this.x, this.y, nextX, nextY, 16);
      this.x = res.x;
      this.y = res.y;
      this.knockbackVx *= Math.max(0, 1 - 10 * dt);
      this.knockbackVy *= Math.max(0, 1 - 10 * dt);
    }

    if (this.isDead) {
      // Clear all active hitboxes
      this.activeHitboxes = [];
      return;
    }

    const distToPlayer = Math.hypot(playerX - this.x, playerY - this.y);
    const angleToPlayer = Math.atan2(playerY - this.y, playerX - this.x);

    // Clean expired hitboxes
    const now = Date.now();
    for (let i = this.activeHitboxes.length - 1; i >= 0; i--) {
      if (now - this.activeHitboxes[i].createdAt >= this.activeHitboxes[i].durationMs) {
        this.activeHitboxes.splice(i, 1);
      }
    }

    // Update enemy arrows
    for (let i = this.arrows.length - 1; i >= 0; i--) {
      const arrow = this.arrows[i];
      arrow.x += arrow.vx * dt;
      arrow.y += arrow.vy * dt;
      if (map.isSolid(arrow.x, arrow.y, 6)) {
        this.arrows.splice(i, 1);
      }
    }

    // AI State Machine Logic
    switch (this.state) {
      case "idle":
      case "patrol": {
        if (!playerDead && distToPlayer <= this.stats.detectRadius) {
          this.state = "chase";
          this.stateTimeMs = 0;
          break;
        }

        this.patrolTimerMs -= dtMs;
        if (this.patrolTimerMs <= 0) {
          this.patrolTimerMs = 1500 + Math.random() * 2000;
          if (Math.random() < 0.4) {
            this.state = "idle";
            this.patrolVx = 0;
            this.patrolVy = 0;
          } else {
            this.state = "patrol";
            const randAngle = Math.random() * Math.PI * 2;
            const patrolSpeed = this.stats.speed * 0.5;
            this.patrolVx = Math.cos(randAngle) * patrolSpeed;
            this.patrolVy = Math.sin(randAngle) * patrolSpeed;
            this.facingAngle = randAngle;
          }
        }

        if (this.state === "patrol") {
          const nextX = this.x + this.patrolVx * dt;
          const nextY = this.y + this.patrolVy * dt;
          const res = map.resolveMovement(this.x, this.y, nextX, nextY, 16);
          this.x = res.x;
          this.y = res.y;
          this.walkAnimClock += dt * 6;
        }
        break;
      }

      case "chase": {
        if (playerDead) {
          this.state = "idle";
          break;
        }

        this.facingAngle = angleToPlayer;

        // Check if in attack range
        const inAttackRange = distToPlayer <= this.stats.attackRadius;
        if (inAttackRange) {
          // Trigger Attack Wind-up + Telegraph Red Zone
          this.state = "windup";
          this.stateTimeMs = 0;
          this.attackHitSpawned = false;
          this.facingAngle = angleToPlayer; // Lock angle directly to player at start

          // Register warning red zone
          this.currentTelegraphShape = this.buildTelegraphShape();
          telegraphSystem.createTelegraph(this.id, this.currentTelegraphShape, this.stats.telegraphDurationMs);
          anomaliAudio.playSfx("telegraph_alert");
          break;
        }

        // Movement toward or keeping distance (for ranged)
        let moveAngle = angleToPlayer;
        if (this.type === "ranged" && this.stats.preferredDistance) {
          if (distToPlayer < this.stats.preferredDistance - 20) {
            moveAngle = angleToPlayer + Math.PI; // Retreat to keep range
          } else if (distToPlayer <= this.stats.preferredDistance + 20) {
            // Already in good shooting pocket
            this.state = "windup";
            this.stateTimeMs = 0;
            this.attackHitSpawned = false;
            this.facingAngle = angleToPlayer; // Lock angle directly to player at start

            this.currentTelegraphShape = this.buildTelegraphShape();
            telegraphSystem.createTelegraph(this.id, this.currentTelegraphShape, this.stats.telegraphDurationMs);
            anomaliAudio.playSfx("telegraph_alert");
            break;
          }
        }

        const moveVx = Math.cos(moveAngle) * this.stats.speed;
        const moveVy = Math.sin(moveAngle) * this.stats.speed;
        const res = map.resolveMovement(this.x, this.y, this.x + moveVx * dt, this.y + moveVy * dt, 16);
        this.x = res.x;
        this.y = res.y;
        this.walkAnimClock += dt * 8;
        break;
      }

      case "windup": {
        // CRITICAL TELEGRAPH ACCURACY:
        // Position and orientation remain locked to ensure 100% congruence with the ground red zone.
        // When windup ends: RED ZONE IMMEDIATELY DISAPPEARS, then ATTACK begins
        if (this.stateTimeMs >= this.stats.telegraphDurationMs) {
          telegraphSystem.clearTelegraph(this.id);
          this.state = "attack";
          this.stateTimeMs = 0;
          this.attackHitSpawned = false;
        }
        break;
      }

      case "attack": {
        // Spawn damage hitbox
        if (!this.attackHitSpawned) {
          this.attackHitSpawned = true;
          this.spawnAttackHitbox();
        }

        if (this.stateTimeMs >= 220) {
          this.state = "recovery";
          this.stateTimeMs = 0;
        }
        break;
      }

      case "recovery": {
        if (this.stateTimeMs >= this.stats.recoveryDurationMs) {
          this.state = "chase";
          this.stateTimeMs = 0;
        }
        break;
      }

      case "hit": {
        if (this.stateTimeMs >= 220) {
          this.state = "chase";
          this.stateTimeMs = 0;
        }
        break;
      }
    }
  }

  /**
   * Build the appropriate Red Zone Telegraph shape based on enemy archetype
   */
  public buildTelegraphShape(): HitboxShape {
    const s = this.stats.telegraphSize;

    if (this.stats.telegraphShape === "cone") {
      const cone: ConeShape = {
        type: "cone",
        x: this.x,
        y: this.y,
        radius: s.radius || 60,
        angle: this.facingAngle,
        spreadAngle: ((s.angleDeg || 60) * Math.PI) / 180,
      };
      return cone;
    } else if (this.stats.telegraphShape === "rect") {
      // Frontal rectangle projecting along facing angle
      const forwardOffset = (s.length || 100) / 2;
      const rect: RectShape = {
        type: "rect",
        x: this.x + Math.cos(this.facingAngle) * forwardOffset,
        y: this.y + Math.sin(this.facingAngle) * forwardOffset,
        width: s.width || 50,
        height: s.length || 100,
        angle: this.facingAngle - Math.PI / 2, // oriented lengthwise along facing
      };
      return rect;
    } else if (this.stats.telegraphShape === "circle") {
      const circle: CircleShape = {
        type: "circle",
        x: this.x,
        y: this.y,
        radius: s.radius || 50,
      };
      return circle;
    }

    // Default line
    return {
      type: "line",
      x1: this.x,
      y1: this.y,
      x2: this.x + Math.cos(this.facingAngle) * (s.radius || 80),
      y2: this.y + Math.sin(this.facingAngle) * (s.radius || 80),
      thickness: 18,
    };
  }

  /**
   * Spawn physical combat hitbox when attack executes
   */
  private spawnAttackHitbox() {
    if (this.type === "ranged") {
      // Archer launches an arrow projectile with faster velocity
      const arrowSpeed = 380;
      this.arrows.push({
        id: `arrow_${this.id}_${Date.now()}`,
        x: this.x + Math.cos(this.facingAngle) * 18,
        y: this.y + Math.sin(this.facingAngle) * 18,
        vx: Math.cos(this.facingAngle) * arrowSpeed,
        vy: Math.sin(this.facingAngle) * arrowSpeed,
        angle: this.facingAngle,
        damage: this.stats.attack,
        knockback: this.stats.knockbackPower,
        active: true,
      });
      anomaliAudio.playSfx("slash_1");
      return;
    }

    // Melee attack hitbox matches EXACT warning red zone area
    const shape = this.currentTelegraphShape || this.buildTelegraphShape();
    this.activeHitboxes.push({
      id: `enemy_hit_${this.id}_${Date.now()}`,
      sourceId: this.id,
      isPlayer: false,
      shape,
      damage: this.stats.attack,
      knockback: this.stats.knockbackPower,
      hitstopMs: 60,
      hitEntities: new Set<string>(),
      durationMs: 140,
      createdAt: Date.now(),
    });

    anomaliAudio.playSfx(this.type === "heavy" ? "slash_3" : "slash_2");
  }

  /**
   * Render Enemy Sprite and Visual Indicators
   */
  public render(ctx: CanvasRenderingContext2D, now: number) {
    if (this.isDead) return;

    ctx.save();

    // 1. Enemy Shadow
    ctx.fillStyle = "rgba(0, 0, 0, 0.4)";
    const shadowSize = this.type === "heavy" ? 18 : 12;
    ctx.beginPath();
    ctx.ellipse(this.x, this.y + 12, shadowSize, 6, 0, 0, Math.PI * 2);
    ctx.fill();

    // 2. Main Character Rendering
    const isFacingLeft = Math.cos(this.facingAngle) < 0;
    const isFlashing = this.hurtFlashMs > 0 && Math.floor(now * 0.03) % 2 === 0;
    const filter = isFlashing ? "brightness(3)" : undefined;
    let drawnFromAtlas = false;

    if (this.type === "heavy") {
      let frameIdx = 0;
      if (this.state === "windup" || this.state === "attack") {
        frameIdx = Math.min(11, Math.floor((this.stateTimeMs / Math.max(100, this.stats.telegraphDurationMs)) * 12));
      } else if (this.state === "chase" || this.state === "patrol") {
        frameIdx = Math.floor(this.walkAnimClock * 2) % 12;
      } else {
        frameIdx = Math.floor(now * 0.006) % 12;
      }
      drawnFromAtlas = assetManager.drawFrame(
        ctx,
        "enemy",
        "heavy_attack",
        frameIdx,
        this.x,
        this.y,
        isFacingLeft,
        1.0,
        filter,
        1.25
      );
    } else if (this.type === "melee") {
      let frameIdx = 0;
      if (this.state === "windup" || this.state === "attack") {
        frameIdx = Math.min(11, Math.floor((this.stateTimeMs / Math.max(100, this.stats.telegraphDurationMs)) * 12));
      } else if (this.state === "chase" || this.state === "patrol") {
        frameIdx = Math.floor(this.walkAnimClock * 2) % 12;
      } else {
        frameIdx = Math.floor(now * 0.006) % 12;
      }
      drawnFromAtlas = assetManager.drawFrame(
        ctx,
        "enemy",
        "melee_walk",
        frameIdx,
        this.x,
        this.y,
        isFacingLeft,
        1.0,
        filter,
        1.0
      );
    } else if (this.type === "ranged") {
      let frameIdx = 0;
      if (this.state === "windup" || this.state === "attack") {
        frameIdx = Math.min(11, Math.floor((this.stateTimeMs / Math.max(100, this.stats.telegraphDurationMs)) * 12));
      } else if (this.state === "chase" || this.state === "patrol") {
        frameIdx = Math.floor(this.walkAnimClock * 2) % 12;
      } else {
        frameIdx = Math.floor(now * 0.006) % 12;
      }
      drawnFromAtlas = assetManager.drawFrame(
        ctx,
        "enemy",
        "ranged_shoot",
        frameIdx,
        this.x,
        this.y,
        isFacingLeft,
        1.0,
        filter,
        1.0
      );
    } else if (this.type === "fast") {
      let frameIdx = 0;
      if (this.state === "windup" || this.state === "attack") {
        frameIdx = Math.min(11, Math.floor((this.stateTimeMs / Math.max(100, this.stats.telegraphDurationMs)) * 12));
      } else if (this.state === "chase" || this.state === "patrol") {
        frameIdx = Math.floor(this.walkAnimClock * 2) % 12;
      } else {
        frameIdx = Math.floor(now * 0.006) % 12;
      }
      drawnFromAtlas = assetManager.drawFrame(
        ctx,
        "enemy",
        "spear_thrust",
        frameIdx,
        this.x,
        this.y,
        isFacingLeft,
        1.0,
        filter,
        1.0
      );
    }

    if (!drawnFromAtlas) {
      ctx.save();
      ctx.translate(this.x, this.y);

      if (this.hurtFlashMs > 0 && Math.floor(now * 0.03) % 2 === 0) {
        ctx.filter = "brightness(3)";
      }

      if (isFacingLeft) {
        ctx.scale(-1, 1);
      }

      const bobY = (this.state === "chase" || this.state === "patrol") ? Math.sin(this.walkAnimClock) * 2 : 0;

      // Archetype Specific Visual Rendering (Matching the reference art style!)
      if (this.type === "heavy") {
        // --- HEAVY ANOMALY (Horned Mask, Great Axe, Round Shield) ---
        ctx.fillStyle = "#2c1c16";
        ctx.fillRect(-10, -16 + bobY, 20, 20);

        // Horned Helmet
        ctx.fillStyle = "#3a2a22";
        ctx.beginPath();
        ctx.arc(0, -20 + bobY, 9, 0, Math.PI * 2);
        ctx.fill();

        // Horns
        ctx.fillStyle = "#d0c0a0";
        ctx.beginPath();
        ctx.moveTo(-6, -24 + bobY);
        ctx.lineTo(-12, -32 + bobY);
        ctx.lineTo(-4, -26 + bobY);
        ctx.moveTo(6, -24 + bobY);
        ctx.lineTo(12, -32 + bobY);
        ctx.lineTo(4, -26 + bobY);
        ctx.fill();

        // Glowing red eyes / anomaly visor
        ctx.fillStyle = "#ff2222";
        ctx.fillRect(2, -22 + bobY, 4, 2);

        // Round Shield
        ctx.fillStyle = "#5c4028";
        ctx.strokeStyle = "#807060";
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.arc(-8, -8 + bobY, 11, 0, Math.PI * 2);
        ctx.fill();
        ctx.stroke();

        // Great Axe
        ctx.save();
        const axeAngle = this.state === "windup" ? -1.2 : this.state === "attack" ? 0.9 : 0.2;
        ctx.translate(10, -8 + bobY);
        ctx.rotate(axeAngle);
        ctx.fillStyle = "#4a3525";
        ctx.fillRect(-2, -18, 4, 28); // handle
        ctx.fillStyle = "#7a8a92";
        ctx.beginPath();
        ctx.arc(2, -14, 9, -Math.PI / 2, Math.PI / 2);
        ctx.fill(); // blade
        ctx.restore();

      } else if (this.type === "ranged") {
        // --- RANGED ANOMALY (Archer Bandit with Hood & Bow) ---
        ctx.fillStyle = "#2b3824";
        ctx.fillRect(-6, -14 + bobY, 12, 16);

        // Leather Hood
        ctx.fillStyle = "#3e4d35";
        ctx.beginPath();
        ctx.arc(0, -18 + bobY, 7, 0, Math.PI * 2);
        ctx.fill();

        // Mask
        ctx.fillStyle = "#182014";
        ctx.fillRect(1, -18 + bobY, 5, 4);

        // Wooden Bow
        ctx.strokeStyle = "#6d4c28";
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.arc(8, -8 + bobY, 10, -Math.PI / 2, Math.PI / 2);
        ctx.stroke();

      } else if (this.type === "fast") {
        // --- FAST SPEARMAN ANOMALY (Ninja/Spear Lurker) ---
        ctx.fillStyle = "#1e1e24";
        ctx.fillRect(-6, -14 + bobY, 12, 16);

        ctx.fillStyle = "#f5d3b3";
        ctx.beginPath();
        ctx.arc(0, -18 + bobY, 6.5, 0, Math.PI * 2);
        ctx.fill();

        ctx.fillStyle = "#c02828";
        ctx.fillRect(-6, -21 + bobY, 12, 3); // Red headband

        // Long Spear
        ctx.fillStyle = "#5c4028";
        ctx.fillRect(4, -22 + bobY, 3, 34); // shaft
        ctx.fillStyle = "#d0dce0";
        ctx.beginPath();
        ctx.moveTo(5.5, -26 + bobY);
        ctx.lineTo(2, -21 + bobY);
        ctx.lineTo(9, -21 + bobY);
        ctx.closePath();
        ctx.fill(); // spear tip

      } else {
        // --- MELEE ANOMALY (Standard Bandit Axeman) ---
        ctx.fillStyle = "#3b2c26";
        ctx.fillRect(-6, -14 + bobY, 12, 16);

        ctx.fillStyle = "#992222";
        ctx.fillRect(-6, -8 + bobY, 12, 4); // Red sash

        ctx.fillStyle = "#f5d3b3";
        ctx.beginPath();
        ctx.arc(0, -18 + bobY, 6.5, 0, Math.PI * 2);
        ctx.fill();

        ctx.fillStyle = "#221c1a";
        ctx.beginPath();
        ctx.arc(0, -19 + bobY, 7, Math.PI, Math.PI * 2);
        ctx.fill();

        // Hand Axe
        ctx.save();
        const axeRot = this.state === "windup" ? -0.8 : this.state === "attack" ? 0.6 : 0.2;
        ctx.translate(8, -6 + bobY);
        ctx.rotate(axeRot);
        ctx.fillStyle = "#4a3525";
        ctx.fillRect(-1.5, -12, 3, 20);
        ctx.fillStyle = "#8a969c";
        ctx.fillRect(1.5, -12, 7, 6);
        ctx.restore();
      }

      ctx.restore(); // restore character transform
    }

    // 3. Enemy HP Bar above head
    const hpBarW = 28;
    const hpBarH = 4;
    const hpX = this.x - hpBarW / 2;
    const hpY = this.y - 28;
    const hpPct = Math.max(0, this.hp / this.maxHp);

    ctx.fillStyle = "rgba(0, 0, 0, 0.6)";
    ctx.fillRect(hpX - 1, hpY - 1, hpBarW + 2, hpBarH + 2);

    ctx.fillStyle = "#d42b2b";
    ctx.fillRect(hpX, hpY, hpBarW * hpPct, hpBarH);

    ctx.restore();

    // 4. Render Enemy Arrows
    for (const arrow of this.arrows) {
      ctx.save();
      ctx.translate(arrow.x, arrow.y);
      ctx.rotate(arrow.angle);

      ctx.fillStyle = "#5c4028";
      ctx.fillRect(-10, -1, 20, 2); // shaft
      ctx.fillStyle = "#d0dce0";
      ctx.beginPath();
      ctx.moveTo(12, 0);
      ctx.lineTo(8, -3);
      ctx.lineTo(8, 3);
      ctx.closePath();
      ctx.fill(); // arrowhead

      ctx.restore();
    }
  }
}
